import { useAnalytics } from '@monkvision/analytics';
import { Camera, CameraHUDProps } from '@monkvision/camera-web';
import { useI18nSync, useLoadingState, useObjectMemo } from '@monkvision/common';
import {
  BackdropDialog,
  InspectionGallery,
  NavigateToCaptureOptions,
  NavigateToCaptureReason,
} from '@monkvision/common-ui-web';
import { MonkApiConfig } from '@monkvision/network';
import {
  AddDamage,
  CameraConfig,
  ComplianceOptions,
  MonkPicture,
  PhotoCaptureAppConfig,
  PhotoCaptureSightGuidelinesOption,
  PhotoCaptureTutorialOption,
  PhotoCaptureSightTutorialOption,
  Sight,
  VehicleType,
} from '@monkvision/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styles } from './PhotoCapture.styles';
import { PhotoCaptureHUD, PhotoCaptureHUDProps } from './PhotoCaptureHUD';
import {
  useStartTasksOnComplete,
  usePictureTaken,
  useAddDamageMode,
  useUploadQueue,
  usePhotoCaptureImages,
  useAdaptiveCameraConfig,
  useBadConnectionWarning,
  useTracking,
  useCaptureDuration,
} from '../hooks';
import {
  useComplianceAnalytics,
  usePhotoCaptureSightState,
  usePhotoCaptureTutorial,
  usePhotoCaptureSightGuidelines,
  usePhotoCaptureSightTutorial,
  useInspectionComplete,
} from './hooks';
import { useImagesCleanup } from './hooks/useImagesCleanup';

/**
 * Props of the PhotoCapture component.
 */
export interface PhotoCaptureProps
  extends Pick<
      PhotoCaptureAppConfig,
      | keyof CameraConfig
      | 'maxUploadDurationWarning'
      | 'useAdaptiveImageQuality'
      | 'additionalTasks'
      | 'tasksBySight'
      | 'startTasksOnComplete'
      | 'showCloseButton'
      | 'enforceOrientation'
      | 'allowSkipRetake'
      | 'addDamage'
      | 'sightGuidelines'
      | 'enableSightGuidelines'
      | 'enableTutorial'
      | 'allowSkipTutorial'
      | 'enableSightTutorial'
      | 'sightTutorial'
      | 'autoDeletePreviousSightImages'
    >,
    Partial<ComplianceOptions> {
  /**
   * The list of sights to take pictures of. The values in this array should be retreived from the `@monkvision/sights`
   * package.
   */
  sights: Sight[];
  /**
   * The ID of the inspection to add images to. Make sure that the user that created the inspection if the same one as
   * the one described in the auth token in the `apiConfig` prop.
   */
  inspectionId: string;
  /**
   * The api config used to communicate with the API. Make sure that the user described in the auth token is the same
   * one as the one that created the inspection provided in the `inspectionId` prop.
   */
  apiConfig: MonkApiConfig;
  /**
   * The vehicle type of the inspection.
   */
  vehicleType?: VehicleType;
  /**
   * Callback called when the user clicks on the Close button. If this callback is not provided, the button will not be
   * displayed on the screen.
   */
  onClose?: () => void;
  /**
   * Callback called when inspection capture is complete.
   */
  onComplete?: () => void;
  /**
   * Callback called when a picture has been taken by the user.
   */
  onPictureTaken?: (picture: MonkPicture) => void;
  /**
   * Callback called when the user clicks on the gallery button.
   * When this callback is provided, the gallery screen will be handled externally.
   */
  onGalleryPress?: () => void;
  /**
   * Boolean indicating if beauty shot extraction should be enabled or not. It should only be enabled if PhotoCapture is
   * used combined with VideoCatpure, and if the user wants to extract beauty shots from the videos taken with
   * VideoCapture.
   *
   * @default false
   */
  enableBeautyShotExtraction?: boolean;
  /**
   * The language to be used by this component.
   *
   * @default en
   */
  lang?: string;
  /**
   * Custom label for validate button in gallery view.
   */
  validateButtonLabel?: string;
}

enum PhotoCaptureScreen {
  CAMERA = 'camera',
  GALLERY = 'gallery',
}

// No ts-doc for this component : the component exported is PhotoCaptureHOC
export function PhotoCapture({
  sights,
  inspectionId,
  apiConfig,
  additionalTasks,
  tasksBySight,
  startTasksOnComplete = true,
  onClose,
  onComplete,
  onPictureTaken,
  maxUploadDurationWarning = 15000,
  showCloseButton = false,
  enableCompliance = true,
  enableCompliancePerSight,
  complianceIssues,
  complianceIssuesPerSight,
  customComplianceThresholds,
  customComplianceThresholdsPerSight,
  useLiveCompliance = false,
  allowSkipRetake = false,
  addDamage = AddDamage.PART_SELECT,
  sightGuidelines,
  enableTutorial = PhotoCaptureTutorialOption.FIRST_TIME_ONLY,
  allowSkipTutorial = true,
  enableSightTutorial = PhotoCaptureSightTutorialOption.DISABLED,
  sightTutorial,
  enableSightGuidelines = PhotoCaptureSightGuidelinesOption.EPHEMERAL,
  useAdaptiveImageQuality = true,
  lang,
  enforceOrientation,
  validateButtonLabel,
  vehicleType = VehicleType.SEDAN,
  autoDeletePreviousSightImages = true,
  onGalleryPress,
  enableBeautyShotExtraction,
  ...initialCameraConfig
}: PhotoCaptureProps) {
  useI18nSync(lang);
  const complianceOptions: ComplianceOptions = useObjectMemo({
    enableCompliance,
    enableCompliancePerSight,
    complianceIssues,
    complianceIssuesPerSight,
    useLiveCompliance,
    customComplianceThresholds,
    customComplianceThresholdsPerSight,
  });
  const { t } = useTranslation();
  const [currentScreen, setCurrentScreen] = useState(PhotoCaptureScreen.CAMERA);
  const analytics = useAnalytics();
  const loading = useLoadingState();
  const handleOpenGallery = () => {
    if (onGalleryPress) {
      onGalleryPress();
    } else {
      setCurrentScreen(PhotoCaptureScreen.GALLERY);
      analytics.trackEvent('Gallery Opened');
    }
  };
  const addDamageHandle = useAddDamageMode({
    addDamage,
    handleOpenGallery,
  });
  useTracking({ inspectionId, authToken: apiConfig.authToken });
  const { setIsInitialInspectionFetched } = useComplianceAnalytics({ inspectionId, sights });
  const { adaptiveCameraConfig, uploadEventHandlers: adaptiveUploadEventHandlers } =
    useAdaptiveCameraConfig({
      initialCameraConfig,
      useAdaptiveImageQuality,
    });
  const startTasks = useStartTasksOnComplete({
    inspectionId,
    apiConfig,
    sights,
    additionalTasks,
    tasksBySight,
    startTasksOnComplete,
    loading,
  });
  const onLastSightTaken = () => {
    if (onGalleryPress) {
      onGalleryPress();
    } else {
      setCurrentScreen(PhotoCaptureScreen.GALLERY);
    }
  };
  const { currentTutorialStep, goToNextTutorialStep, closeTutorial } = usePhotoCaptureTutorial({
    enableTutorial,
    enableSightGuidelines,
    enableSightTutorial,
  });
  const { showSightTutorial, toggleSightTutorial } = usePhotoCaptureSightTutorial();
  const sightState = usePhotoCaptureSightState({
    inspectionId,
    captureSights: sights,
    apiConfig,
    loading,
    onLastSightTaken,
    tasksBySight,
    complianceOptions,
    setIsInitialInspectionFetched,
    toggleSightTutorial,
  });
  const { showSightGuidelines, handleDisableSightGuidelines } = usePhotoCaptureSightGuidelines({
    enableSightGuidelines,
  });
  const {
    isBadConnectionWarningDialogDisplayed,
    closeBadConnectionWarningDialog,
    uploadEventHandlers: badConnectionWarningUploadEventHandlers,
  } = useBadConnectionWarning({ maxUploadDurationWarning });
  const { cleanupEventHandlers } = useImagesCleanup({
    inspectionId,
    apiConfig,
    autoDeletePreviousSightImages,
  });
  const uploadQueue = useUploadQueue({
    inspectionId,
    apiConfig,
    additionalTasks,
    complianceOptions,
    eventHandlers: [
      adaptiveUploadEventHandlers,
      badConnectionWarningUploadEventHandlers,
      cleanupEventHandlers,
    ],
  });
  const images = usePhotoCaptureImages(inspectionId);
  const handlePictureTaken = usePictureTaken({
    captureState: sightState,
    addDamageHandle,
    uploadQueue,
    tasksBySight,
    onPictureTaken,
  });
  const { updateDuration } = useCaptureDuration({
    inspectionId,
    apiConfig,
    isInspectionCompleted: sightState.isInspectionCompleted,
  });
  const { handleInspectionCompleted } = useInspectionComplete({
    startTasks,
    sightState,
    loading,
    startTasksOnComplete,
    onUpdateDuration: updateDuration,
    onComplete,
  });
  const handleGalleryBack = () => setCurrentScreen(PhotoCaptureScreen.CAMERA);
  const handleNavigateToCapture = (options: NavigateToCaptureOptions) => {
    if (options.reason === NavigateToCaptureReason.ADD_DAMAGE) {
      addDamageHandle.handleAddDamage();
    } else if (options.reason === NavigateToCaptureReason.CAPTURE_SIGHT) {
      const selectedSight = sights.find((sight) => sight.id === options.sightId);
      if (!selectedSight) {
        return;
      }
      sightState.selectSight(selectedSight);
    } else if (options.reason === NavigateToCaptureReason.RETAKE_PICTURE) {
      sightState.retakeSight(options.sightId);
    }
    setCurrentScreen(PhotoCaptureScreen.CAMERA);
  };
  const hudProps: Omit<PhotoCaptureHUDProps, keyof CameraHUDProps> = {
    sights,
    selectedSight: sightState.selectedSight,
    sightsTaken: sightState.sightsTaken,
    lastPictureTakenUri: sightState.lastPictureTakenUri,
    mode: addDamageHandle.mode,
    vehicleParts: addDamageHandle.vehicleParts,
    onOpenGallery: handleOpenGallery,
    onSelectSight: sightState.selectSight,
    onRetakeSight: sightState.retakeSight,
    onAddDamage: addDamageHandle.handleAddDamage,
    onAddDamagePartsSelected: addDamageHandle.handleAddDamagePartsSelected,
    onCancelAddDamage: addDamageHandle.handleCancelAddDamage,
    onRetry: sightState.retryLoadingInspection,
    loading,
    onClose,
    inspectionId,
    showCloseButton,
    images,
    addDamage,
    onValidateVehicleParts: addDamageHandle.handleValidateVehicleParts,
    sightGuidelines,
    showSightGuidelines,
    currentTutorialStep,
    onNextTutorialStep: goToNextTutorialStep,
    onCloseTutorial: closeTutorial,
    onDisableSightGuidelines: handleDisableSightGuidelines,
    allowSkipTutorial,
    enforceOrientation,
    vehicleType,
    sightTutorial,
    enableSightTutorial,
    showSightTutorial,
    toggleSightTutorial,
  };

  return (
    <div style={styles['container']}>
      {currentScreen === PhotoCaptureScreen.CAMERA && (
        <Camera
          HUDComponent={PhotoCaptureHUD}
          onPictureTaken={handlePictureTaken}
          hudProps={hudProps}
          {...adaptiveCameraConfig}
        />
      )}
      {currentScreen === PhotoCaptureScreen.GALLERY && (
        <InspectionGallery
          inspectionId={inspectionId}
          apiConfig={apiConfig}
          captureMode={true}
          lang={lang}
          showBackButton={true}
          sights={sights}
          allowSkipRetake={allowSkipRetake}
          enableBeautyShotExtraction={enableBeautyShotExtraction}
          onBack={handleGalleryBack}
          onNavigateToCapture={handleNavigateToCapture}
          onValidate={handleInspectionCompleted}
          addDamage={addDamage}
          validateButtonLabel={validateButtonLabel}
          isInspectionCompleted={sightState.isInspectionCompleted}
          {...complianceOptions}
        />
      )}
      <BackdropDialog
        show={isBadConnectionWarningDialogDisplayed}
        showCancelButton={false}
        dialogIcon='warning-outline'
        dialogIconPrimaryColor='caution-base'
        message={t('photo.badConnectionWarning.message')}
        confirmLabel={t('photo.badConnectionWarning.confirm')}
        onConfirm={closeBadConnectionWarningDialog}
      />
    </div>
  );
}
